import React, { useState, useEffect } from 'react';
import { FaTrash, FaEdit, FaEye, FaSadCry,
  FaBrain,
  FaMagic,
  FaRocket,
  FaDog,
  FaCat,
  FaBook,
  FaMusic,
  FaPuzzlePiece,
  FaPlay
  } from 'react-icons/fa';
import {IoIosAddCircleOutline, IoMdTrash, IoMdCreate} from 'react-icons/io';
import EditarTest from './EditarTest';
import AñadirTest from './AnadirTest';
import VerTest from './VerTest';
import styles from './Test.module.css';
import Swal from 'sweetalert2'; 
import {useAuth} from '../../Auth/AuthContext'; 

const iconMap = {
  FaTrash: FaTrash,
  FaEdit: FaEdit,
  FaEye: FaEye,
  FaSadCry: FaSadCry,
  FaBrain: FaBrain,
  FaMagic: FaMagic,
  FaRocket: FaRocket,
  FaDog: FaDog,
  FaCat: FaCat,
  FaBook: FaBook,
  FaMusic: FaMusic,
  FaPuzzlePiece: FaPuzzlePiece,
};

const Test = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('list');
  const [editingTestId, setEditingTestId] = useState(null);
  const [viewingTest, setViewingTest] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const {getToken} = useAuth(); 
  const token = getToken(); 

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/test/`, { 
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      setTests(data);
      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar los tests. Por favor, intenta de nuevo más tarde.');
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setCurrentView('add');
  };

  const handleEdit = (testId) => {
    setEditingTestId(testId);
    setShowEditModal(true);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setEditingTestId(null);
    setViewingTest(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingTestId(null);
  };

  const handleTestUpdated = () => {
    fetchTests();
    setShowEditModal(false);
    setEditingTestId(null);
  };

  const handleTestAdded = () => {
    fetchTests();
  };

  const handleDelete = async (testId) => {
      const confirmDelete = await Swal.fire({
        title: '¿Eliminar test?',
        text: 'Esta acción no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#242b57',
        customClass: {
          popup: 'popupbonito'
        },
        });

      if (!confirmDelete.isConfirmed) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/test/delete/${testId}`, {
          method: 'DELETE',
          headers: { 
                    'Authorization': `Bearer ${token}`,
          }
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const responseData = await response.json();

        let title, text, icon;
        
        if (responseData.type === 'deactivated') {
          title = 'Test Desactivado';
          text = responseData.reason || 'El test fue desactivado debido a sesiones existentes';
          icon = 'warning';
        } else {
          title = 'Test Eliminado';
          text = 'El test ha sido eliminado permanentemente';
          icon = 'success';
        }

        Swal.fire({
          title: title,
          text: text,
          icon: icon,
          timer: 3000,
          showConfirmButton: false, 
          customClass: {
            popup: 'popupbonito', 
          },
        });

        await fetchTests();
      } catch (error) {
        console.error('Error:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo eliminar el test',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#242b57', 
          customClass: { 
            popup: 'popupbonito', 
          },
          });
      }
  };

  const handleView = (testId) => {
    const test = tests.find(t => t.te_clave === testId);
    if (test) {
      setViewingTest(test);
      setCurrentView('view');
    }
  };

  const handleReactivate = async (testId) => {
    const confirmReactivate = await Swal.fire({
      title: '¿Reactivar test?',
      text: 'El test volverá a estar disponible',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Reactivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#242b57',
      customClass: {
        popup: 'popupbonito'
      },
    });

    if (!confirmReactivate.isConfirmed) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/test/reactivate/${testId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      Swal.fire({
        title: 'Test Reactivado',
        text: 'El test ha sido reactivado correctamente',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false, 
        customClass: {
          popup: 'popupbonito', 
        },
      });

      await fetchTests();
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo reactivar el test',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#242b57', 
        customClass: { 
          popup: 'popupbonito', 
        },
      });
    }
  };

  // Función para cerrar modal al hacer clic en el overlay
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseEditModal();
    }
  };

  if (currentView === 'add') {
    return (
      <AñadirTest
        onBack={handleBackToList}
        onTestAdded={handleTestAdded}
      />
    );
  }

  if (currentView === 'view') {
    return (
      <VerTest
        test={viewingTest}
        onBack={handleBackToList}
      />
    );
  }

if (loading) {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingContent}>
        <div className={styles.loadingSpinner}></div>
        <p className={styles.loadingText}>Cargando test...</p>
      </div>
    </div>
  );
}

  if (error) {
      return (
          <div className={styles.errorAlert} role="alert">
              <strong>Error: </strong>
              <span>{error}</span>
          </div>
      ); 
  }

  // Lista de tests
  return (
    <div className={styles.testContainer}>
      <header className={styles.testHeader}>
        <h1>Test</h1>
        <p>Aquí encontrarás todos los test disponibles.</p>
      </header>

      <div className={styles.addTest}>
        <button className={styles.btnAddTest} onClick={handleAdd}>
          <IoIosAddCircleOutline />
          Añadir Test
        </button>
      </div>

      {tests.length === 0 ? (
        <div className={styles.emptyState}>
          <div>
            <FaSadCry className={styles.emptyIcon} />
          </div>
          <h3>No hay tests disponibles</h3>
          <p>Agrega un primer test :D</p>
        </div>
      ) : (
        <div className={styles.testsGrid}>
          {tests.map((test) => (
            <div 
              key={test.te_clave} 
              className={`${styles.testCard} ${!test.te_activo ? styles.inactiveTest : ''}`}
              style={{
                opacity: test.te_activo ? 1 : 0.5,
                cursor: test.te_activo ? 'default' : 'default'
              }}
            >
              <div className={styles.cardActions}>
                {test.te_activo ? (
                  <>
                    <button
                      className={styles.btnView}
                      onClick={() => handleView(test.te_clave)}
                      title="Ver detalles"
                    >
                      <FaEye/>
                    </button>
                    <button
                      className={styles.btnEdit}
                      onClick={() => handleEdit(test.te_clave)}
                      title="Editar test"
                    >
                      <IoMdCreate/>
                    </button>
                    <button
                      className={styles.btnDelete}
                      onClick={() => handleDelete(test.te_clave)}
                      title="Eliminar test"
                    >
                      <IoMdTrash/>
                    </button>
                  </>
                ) : (
                  <button
                    className={styles.btnEdit}
                    onClick={() => handleReactivate(test.te_clave)}
                    title="Reactivar test"
                  >
                    <FaPlay/>
                  </button>
                )}
              </div>

              <div className={styles.cardIcon}>
                {iconMap[test.te_icon] ? (
                  React.createElement(iconMap[test.te_icon], { className: styles.customIcon })
                ) : (
                  <FaBrain className={styles.customIcon} />
                )}
              </div>

              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>
                  {test.te_nombre}
                  {!test.te_activo && <span className={styles.inactiveLabel}> (Inactivo)</span>}
                </h3>
                <p className={styles.cardDescription}>{test.te_descripcion}</p>

                <div className={styles.cardInfo}>
                  {test.te_tiempo_estimado && test.te_tiempo_estimado !== 0 && test.te_tiempo_estimado !== '0' && (
                    <div className={styles.infoItem}>
                      <span>{test.te_tiempo_estimado} min</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal para Editar Test */}
      {showEditModal && (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
          <div className={styles.modalContent}>
            <EditarTest
              testId={editingTestId}
              onBack={handleCloseEditModal}
              onTestUpdated={handleTestUpdated}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Test;